import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { eventAPI } from '../api';
import { AuthContext } from '../AuthContext';
import { t } from '../i18n';

const supportedLocales = new Set([
  'ar', 'bg', 'ca', 'cs', 'de', 'el', 'en', 'es', 'fa', 'fi', 'fr', 'he',
  'hr', 'it', 'ja', 'ko', 'lt', 'no', 'pl', 'pt-BR', 'ro', 'ru', 'sk', 'sl',
  'sr', 'sv', 'tr', 'uz', 'vi', 'zh-Hans', 'zh-Hant'
]);

const getCurrentLanguage = () => {
  try {
    return localStorage.getItem('language') || navigator.language || 'en';
  } catch (e) {
    return navigator.language || 'en';
  }
};

const getLocale = () => {
  const language = getCurrentLanguage();
  if (supportedLocales.has(language)) return language;
  const baseLanguage = language.split('-')[0];
  return supportedLocales.has(baseLanguage) ? baseLanguage : 'en';
};

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [event, setEvent] = useState(null);
  const [responses, setResponses] = useState([]);
  const [userResponse, setUserResponse] = useState(null);
  const [cancelMessage, setCancelMessage] = useState('');
  // hover state for might button removed; styling handled via CSS class
  const [hoverRepost, setHoverRepost] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState({ open: false, message: '' });

  useEffect(() => {
    loadEvent();
    loadResponses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Poll for updates to the event and responses so the page stays in sync
  useEffect(() => {
    const iv = setInterval(() => {
      loadEvent();
      loadResponses();
    }, 10000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadEvent = async () => {
    try {
      const response = await eventAPI.getById(id);
      setEvent(response.data);
    } catch (err) {
      console.error('Failed to load event:', err);
      // Redirect to calendar if event not accessible
      navigate('/calendar');
    }
  };

  const loadResponses = async () => {
    try {
      const response = await eventAPI.getResponses(id);
      setResponses(response.data);
      
      // Find current user's response
      const myResponse = response.data.find(r => r.user.id === user.id);
      setUserResponse(myResponse);
    } catch (err) {
      console.error('Failed to load responses:', err);
      // If responses cannot be loaded, redirect to calendar
      navigate('/calendar');
    }
  };

  const handleRespond = async (responseType) => {
    try {
      // If clicking on the same response, remove it (toggle off)
      if (userResponse && userResponse.response === responseType) {
        await eventAPI.removeResponse(id);
        loadResponses();
        return;
      }
      
      await eventAPI.respond(id, responseType);
      loadResponses();
    } catch (err) {
      console.error('Failed to respond:', err);
    }
  };

  const handleRetweet = async () => {
    try {
      const res = await eventAPI.repost(id);
      // navigate to the new cloned event
      const newEvent = res.data;
      window.location.href = `/event/${newEvent.copy_id || newEvent.id}`;
    } catch (err) {
      console.error('Failed to retweet:', err);
      alert(t('mustRespondWillBeforeReposting'));
    }
  };

  const generateGoogleCalendarUrl = () => {
    if (!event) return '';

    // Format dates for Google Calendar (YYYYMMDDTHHmmssZ in UTC)
    const formatDateForGoogle = (date) => {
      const d = new Date(date);
      return d.toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
    };

    const startDt = new Date(event.date_time);
    const endDt = event.ends_at 
      ? new Date(event.ends_at) 
      : (event.duration_minutes 
        ? new Date(startDt.getTime() + Number(event.duration_minutes) * 60000) 
        : new Date(startDt.getTime() + 60*60000));

    const title = encodeURIComponent(event.title || t('event'));
    const dates = `${formatDateForGoogle(startDt)}/${formatDateForGoogle(endDt)}`;
    const details = encodeURIComponent(event.description || '');
    const location = encodeURIComponent(event.place_name || event.place_address || event.place || '');

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${dates}&details=${details}&location=${location}`;
  };

  if (!event) return <div className="container">{t('loading')}</div>;

  const isHost = event.creator.id === user.id;
  const willResponses = responses.filter(r => r.response === 'will');
  const mightResponses = responses.filter(r => r.response === 'might');
  
  // Display hosts from the API
  const hosts = event.hosts || [event.creator.username];

  return (
    <div className="container">
      <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <button className="btn btn-secondary" onClick={() => navigate('/calendar')}>{t('backToCalendar')}</button>
        <a
          href={generateGoogleCalendarUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn-primary"
        >
          {t('addToGoogleCalendar')}
        </a>
      </div>
      <div className="card">
        <h1>{event.title}</h1>
        {/* Activity removed */}
        <p>
          <strong>{t('when')}:</strong>{' '}
          {(() => {
            const startDt = new Date(event.date_time);
            const endDt = event.ends_at ? new Date(event.ends_at) : (event.duration_minutes ? new Date(startDt.getTime() + Number(event.duration_minutes) * 60000) : new Date(startDt.getTime() + 60*60000));
            const dateStr = startDt.toLocaleDateString(getLocale(), { day: '2-digit', month: '2-digit', year: 'numeric' });
            const startStr = startDt.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
            const endStr = endDt.toLocaleTimeString(getLocale(), { hour: '2-digit', minute: '2-digit' });
            return (<>{dateStr} {startStr}{' -> '}{endStr}</>);
          })()}
        </p>
        <p>
          <strong>{t('where')}:</strong>{' '}
          {(() => {
            const label = event.place_name || event.place || event.place_address || '';
            if (!label) {
              return null;
            }
            const addressQuery = encodeURIComponent(event.place_address || event.place || label);
            const lat = event.place_lat;
            const lng = event.place_lng;
            const webUrl = lat && lng
              ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`
              : `https://www.google.com/maps/search/?api=1&query=${addressQuery}`;

            return (
              <a
                href="#"
                onClick={(ev) => { ev.preventDefault(); window.open(webUrl, '_blank', 'noopener'); }}
                className="text-link"
                aria-label={label || t('openLocationInGoogleMaps')}
              >
                {label || webUrl}
              </a>
            );
          })()}
        </p>
        <p><strong>{t('description')}:</strong> {event.description}</p>
        <p><strong>{t('hosts')}:</strong> {hosts.map((h, i) => (
          <span key={h}>
            <Link to={`/users/${encodeURIComponent(h)}`} className="text-link">{h}</Link>{i < hosts.length - 1 ? ', ' : ''}
          </span>
        ))}</p>
        {/* clone_key is internal only; no visible UI */}
      </div>

      {!isHost && (
        <div className="card">
          <h2>{t('yourResponse')}</h2>
          {userResponse && <p>{t('currentResponse')}: <strong>{t(`response_${userResponse.response}`)}</strong></p>}

          <>
            <div style={{ display: 'flex', gap: '10px', marginTop: '10px', alignItems: 'center' }}>
              <button
                className={"btn btn-success" + (userResponse?.response === 'will' ? ' btn-selected' : '')}
                onClick={() => handleRespond('will')}
                style={{
                  minWidth: '110px',
                  minHeight: '40px',
                  transition: 'box-shadow 140ms ease'
                }}
              >
                {t('willRunIntoYou')}
              </button>
              <button
                className={"btn btn-might" + (userResponse?.response === 'might' ? ' btn-selected' : '')}
                onClick={() => handleRespond('might')}
                style={{
                  minWidth: '110px',
                  minHeight: '40px',
                  transition: 'box-shadow 140ms ease'
                }}
              >
                {t('mightDropBy')}
              </button>
              <button
                className={"btn btn-secondary" + (userResponse?.response === 'wont' ? ' btn-selected' : '')}
                onClick={() => handleRespond('wont')}
                style={{
                  minWidth: '110px',
                  minHeight: '40px',
                  transition: 'box-shadow 140ms ease'
                }}
              >
                {t('wontCome')}
              </button>
            </div>

            {userResponse?.response === 'will' && (
              <div style={{ marginTop: 10 }}>
                <button
                  className="btn btn-repost"
                  onClick={handleRetweet}
                  onMouseEnter={() => setHoverRepost(true)}
                  onMouseLeave={() => setHoverRepost(false)}
                  style={{
                    backgroundColor: hoverRepost ? '#c89f1a' : '#DAA520',
                    color: '#222',
                    border: 'none',
                    minWidth: '110px',
                    minHeight: '40px',
                    transition: 'box-shadow 140ms ease, background-color 120ms ease'
                  }}
                >
                  {t('repostToMyFriends')}
                </button>
              </div>
            )}
          </>
        </div>
      )}

      {isHost && (
        <div className="card">
          <h2>{t('responses')}</h2>
          <h3>{t('willAttend')} ({willResponses.length})</h3>
          {willResponses.map(r => (
            <div key={r.id} style={{ padding: '5px', borderBottom: '1px solid #eee' }}>
              {r.user.username}
              {r.can_retweet && <span style={{ marginLeft: '10px', color: 'blue' }}>({t('reposted')})</span>}
            </div>
          ))}
          
          <h3 style={{ marginTop: '20px' }}>{t('mightAttend')} ({mightResponses.length})</h3>
          {mightResponses.map(r => (
            <div key={r.id} style={{ padding: '5px', borderBottom: '1px solid #eee' }}>
              {r.user.username}
            </div>
          ))}
        </div>
      )}

      {isHost && (
        <div className="card">
          <h2>{t('cancelEvent')}</h2>
          <p>{t('cancellationEmailNoticeBefore')} ({willResponses.length}) {t('cancellationEmailNoticeAfter')}</p>
          <textarea
            value={cancelMessage}
            onChange={(e) => setCancelMessage(e.target.value)}
            placeholder={t('optionalCancellationMessage')}
            maxLength={1000}
            style={{ width: '100%', minHeight: 120, marginTop: 8 }}
          />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button className="btn btn-danger" onClick={() => setShowCancelModal(true)}>{t('confirmCancelEvent')}</button>
            <button className="btn btn-secondary" onClick={() => setCancelMessage('')}>{t('clear')}</button>
          </div>
        </div>
      )}

      {/* Custom cancel confirmation modal (replaces window.confirm) */}
      {showCancelModal && (
        <div className="crop-modal" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
          <div className="crop-backdrop" onClick={() => setShowCancelModal(false)} />
          <div className="crop-panel" style={{ maxWidth: 560 }}>
            <h3 id="cancel-title">{t('confirmCancellation')}</h3>
            <p>{t('confirmCancellationMessage')}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button
                className="btn btn-danger"
                disabled={isCancelling}
                onClick={async () => {
                  setIsCancelling(true);
                  try {
                    const res = await eventAPI.cancel(id, { message: cancelMessage });
                    // show non-blocking success modal instead of native alert
                    setShowSuccessModal({ open: true, message: `${t('eventCancelledNotified')} ${res.data.notified_count} ${t('attendees')}.` });
                    setShowCancelModal(false);
                    setIsCancelling(false);
                  } catch (err) {
                    console.error('Failed to cancel event:', err);
                    alert(t('failedToCancelEvent'));
                    setIsCancelling(false);
                    setShowCancelModal(false);
                  }
                }}
              >
                {t('confirmCancelEvent')}
              </button>
              <button className="btn btn-secondary" onClick={() => setShowCancelModal(false)} disabled={isCancelling}>{t('back')}</button>
            </div>
          </div>
        </div>
      )}

      {showSuccessModal.open && (
        <div className="crop-modal" role="dialog" aria-modal="true" aria-labelledby="success-title">
          <div className="crop-backdrop" onClick={() => setShowSuccessModal({ open: false, message: '' })} />
          <div className="crop-panel" style={{ maxWidth: 560 }}>
            <h3 id="success-title">{t('cancelled')}</h3>
            <p>{showSuccessModal.message}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn btn-primary" onClick={() => { window.location.href = '/'; }}>{t('ok')}</button>
            </div>
          </div>
        </div>
      )}

      {/* 'Who's Hosting' card removed per UX change */}
    </div>
  );
};

export default EventDetail;
